/**
 * Sync Global Store Actions
 *
 * Actions for syncing machine context with Zustand global store
 */

import { parseDateFormat } from "@/components/InsuranceStepper/InsuranceStepper.helper";
import { normalizePhoneInput } from "@/util/formatters";
import type { LanguageCode } from "@frontend/lib/graphql/generated-gql";
import type { FormInsuranceData } from "@frontend/lib/schemas/clientSchema";
import { useBoundStoreBase } from "@frontend/lib/stores/globalStore";
import type { InsuranceConnectorContext } from "../types";

/**
 * Backend match/disambiguate endpoints return `consumer.dob` as an ISO-8601
 * string (e.g. "2003-07-13T00:00:00.000Z") or plain "yyyy-mm-dd". The global
 * store and `BasicInfoReviewCard` both expect "MM-dd-yyyy" (see
 * `parse(userData.dob, "MM-dd-yyyy", …)` there). Convert string-side so we
 * never construct a JS `Date` from the ISO string — that would drift by a day
 * for users west of UTC at midnight.
 */
function normalizeBackendDobToMMDDYYYY(dob: string): string {
  const datePart = dob.split("T")[0];
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (match) {
    const [, y, m, d] = match;
    return `${m}-${d}-${y}`;
  }
  // Already MM-dd-yyyy / MM/dd/yyyy — let parseDateFormat normalize.
  try {
    return parseDateFormat(dob);
  } catch {
    return dob;
  }
}

/**
 * Sync insurance data to global store
 */
export const syncInsuranceToStore = ({
  context,
}: { context: InsuranceConnectorContext }) => {
  const { setInsurances } = useBoundStoreBase.getState();

  const insurances: FormInsuranceData[] = [];
  const results = context.lastVerificationResults;

  if (context.primaryInsurance) {
    const verificationResult = results?.find(
      (r) =>
        r.memberId === context.primaryInsurance?.memberId &&
        r.payer === context.primaryInsurance?.payer,
    );
    insurances.push({
      payer: context.primaryInsurance.payer,
      memberId: context.primaryInsurance.memberId,
      state: context.primaryInsurance.insuredState ?? "",
      status: verificationResult?.status,
      planName:
        context.primaryInsurance.planName ?? verificationResult?.planName,
    });
  }

  if (context.secondaryInsurance) {
    const verificationResult = results?.find(
      (r) =>
        r.memberId === context.secondaryInsurance?.memberId &&
        r.payer === context.secondaryInsurance?.payer,
    );
    insurances.push({
      payer: context.secondaryInsurance.payer,
      memberId: context.secondaryInsurance.memberId,
      state: context.secondaryInsurance.insuredState ?? "",
      status: verificationResult?.status,
      planName:
        context.secondaryInsurance.planName ?? verificationResult?.planName,
    });
  }

  setInsurances(insurances);
};

/**
 * Sync consumer data to global store
 * Uses full consumer data from disambiguation if available, otherwise falls back to form data
 */
export const syncConsumerToStore = ({
  context,
}: { context: InsuranceConnectorContext }) => {
  const { setConsumer, eligibilityCheckResult } = useBoundStoreBase.getState();

  // Preserve the eligibility state set before the insurance flow. Read from
  // eligibilityCheckResult (the explicit output of the eligibility form) rather
  // than consumer.state (a general field that could carry stale persisted data
  // from a previous session or a different code path). Only apply when
  // eligibility actually passed — not when it was skipped or failed.
  const preEligibilityState =
    eligibilityCheckResult?.result === "passed"
      ? eligibilityCheckResult.state
      : undefined;

  // Use full consumer data from disambiguation if available
  if (context.consumerData) {
    setConsumer({
      id: context.consumerData.id,
      fname: context.consumerData.firstname,
      lname: context.consumerData.lastname,
      dob: normalizeBackendDobToMMDDYYYY(context.consumerData.dob),
      sex: undefined,
      ...(preEligibilityState && { state: preEligibilityState }),
    });
  } else if (context.firstName && context.lastName && context.birthday) {
    // Fallback to form data if consumer data not available
    setConsumer({
      id: context.consumerId || "",
      fname: context.firstName,
      lname: context.lastName,
      dob: parseDateFormat(context.birthday),
      sex: undefined,
      ...(preEligibilityState && { state: preEligibilityState }),
    });
  }
};

/**
 * Sync user data to global store
 * Uses full consumer data from disambiguation if available, otherwise falls back to form data
 */
export const syncUserToStore = ({
  context,
}: { context: InsuranceConnectorContext }) => {
  const { setUser } = useBoundStoreBase.getState();

  // Use full consumer data from disambiguation if available
  const phone = normalizePhoneInput(context.phoneNumber || "");

  if (context.consumerData) {
    setUser({
      id: context.userId || context.consumerData.userId || "",
      fname: context.consumerData.firstname,
      lname: context.consumerData.lastname,
      phone,
      email: context.consumerData.email || context.userEmail || "",
      verifiedFlag: context.verified || false,
      primaryLanguage: "en" as LanguageCode,
      orgId: context.orgId,
    });
  } else if (context.firstName && context.lastName) {
    setUser({
      id: context.userId || "",
      fname: context.firstName,
      lname: context.lastName,
      phone,
      email: context.userEmail || "",
      verifiedFlag: context.verified || false,
      primaryLanguage: "en" as LanguageCode,
      orgId: context.orgId,
    });
  }
};

/**
 * Sync a freshly matched consumer's identity (post `matchByPhoneAndDob` or
 * `disambiguate` success) to the global store WITHOUT syncing insurance.
 *
 * This runs BEFORE OTP verification. We only sync name, DOB, phone, and
 * email so downstream surfaces (review page, cart) know who the consumer
 * is. Insurance data is intentionally withheld until OTP succeeds — syncing
 * it pre-OTP caused the bug where dismissing the OTP dialog left stale
 * insurance in the store, making the UI show insurance as connected.
 */
export const syncMatchedIdentityToStore = ({
  context,
}: { context: InsuranceConnectorContext }) => {
  syncConsumerToStore({ context });
  syncUserToStore({ context });
};

/**
 * @deprecated Use `syncMatchedIdentityToStore` instead. This function syncs
 * insurance to the store before OTP verification, which causes a bug where
 * closing the OTP dialog leaves stale insurance in the store.
 */
export const syncMatchedConsumerToStore = ({
  context,
}: { context: InsuranceConnectorContext }) => {
  syncConsumerToStore({ context });
  syncUserToStore({ context });
  syncInsuranceToStore({ context });
};

/**
 * Sync verified data (consumer + user + insurance + validation flag) to the
 * global store. Only call this when insurance was successfully verified with
 * an `active` outcome — anything less should NOT land in the persisted store,
 * otherwise we end up re-hydrating stale partial state on reload.
 */
export const syncAllToStore = ({
  context,
}: { context: InsuranceConnectorContext }) => {
  syncInsuranceToStore({ context });
  syncConsumerToStore({ context });
  syncUserToStore({ context });

  if (context.lastVerificationResults) {
    const { setLastInsuranceCheckResults, setIsInsuranceValidated } =
      useBoundStoreBase.getState();
    setLastInsuranceCheckResults(context.lastVerificationResults);
    setIsInsuranceValidated(true);
  }
};

/**
 * Sync insurance + consumer + user to the global store for the
 * proceed-with-unverified path.
 *
 * This mirrors `syncAllToStore` EXCEPT it deliberately does NOT set
 * `isInsuranceValidated` to `true` — the insurance was explicitly not
 * verified, and the storefront needs to be able to distinguish "verified
 * and ready to charge" from "pending, bill after review" at checkout /
 * review time.
 *
 * We DO persist the insurance fields (payer, memberId, state, planName),
 * consumer identity, and user identity because downstream surfaces
 * (review page, checkout, appointment creation) read these directly from
 * the store. Without this sync, a user who proceeds with unverified
 * insurance would land on checkout with an empty insurance card even
 * though the storefront already committed to the insured path.
 *
 * `lastInsuranceCheckResults` is also persisted so that on reload we can
 * show the same "review in progress" UI we showed before the refresh.
 */
export const syncUnverifiedToStore = ({
  context,
}: { context: InsuranceConnectorContext }) => {
  syncInsuranceToStore({ context });
  syncConsumerToStore({ context });
  syncUserToStore({ context });

  if (context.lastVerificationResults) {
    const { setLastInsuranceCheckResults } = useBoundStoreBase.getState();
    setLastInsuranceCheckResults(context.lastVerificationResults);
  }
};

/**
 * Sync only the storefront flags (payment mode + pending prices) to the global
 * store. Used by unverified / skip / proceed-with-unverified paths that need
 * to record UI state without implying insurance is verified.
 */
export const syncStorefrontFlagsToStore = ({
  context,
}: { context: InsuranceConnectorContext }) => {
  if (context.storefrontPaymentMode) {
    const { setStorefrontPaymentMode } = useBoundStoreBase.getState();
    setStorefrontPaymentMode(context.storefrontPaymentMode);
  }

  if (context.isPendingPricesMode !== undefined) {
    const { setIsPendingPricesMode } = useBoundStoreBase.getState();
    setIsPendingPricesMode(context.isPendingPricesMode);
  }
};

/**
 * Save eligibility check failure (under minimum age) to global store.
 *
 * Intentionally does NOT write birthday to the `consumer` slice: eligibility
 * failure means the flow did not complete, and leaking partial identity into
 * the persisted store would cause stale data to survive reloads.
 */
export const saveEligibilityFailedUnderAge = ({
  context,
}: { context: InsuranceConnectorContext }) => {
  const { setEligibilityCheckResult, setMinimumAge } =
    useBoundStoreBase.getState();
  setEligibilityCheckResult({
    result: "failed",
    code: "under_aged",
    confirmation: false,
  });
  if (context.minimumAge) {
    setMinimumAge(context.minimumAge);
  }
};

/**
 * Save eligibility check failure (state not available) to global store.
 *
 * Intentionally does NOT write the consumer state to the `consumer` slice
 * (eligibility failure means the flow did not complete, and we do not want
 * partial identity leaking into the persisted store). We DO attach the
 * rejected state to the eligibility result itself so the "Not available in
 * {state}" banner has the value it needs after a reload.
 */
export const saveEligibilityFailedStateNotAvailable = ({
  context,
}: { context: InsuranceConnectorContext }) => {
  const { setEligibilityCheckResult } = useBoundStoreBase.getState();
  setEligibilityCheckResult({
    result: "failed",
    code: "state_not_available",
    confirmation: false,
    state: context.consumerState,
  });
};

/**
 * Save the minor-consent referral outcome to global store.
 *
 * Persisted as a `failed` result so every surface that blocks on
 * `result === "failed"` (pre-flight checks, product-page booking CTAs)
 * treats a referred 15, 16 or 17 year old exactly like the other rejections,
 * and so reopening the connector routes straight back to the referral
 * screen instead of restarting the eligibility questions.
 *
 * Like the other failure saves, intentionally does NOT write the birthday
 * to the `consumer` slice: the flow did not complete, and partial identity
 * must not leak into the persisted store.
 */
export const saveEligibilityFailedMinorConsentReferral = () => {
  const { setEligibilityCheckResult } = useBoundStoreBase.getState();
  setEligibilityCheckResult({
    result: "failed",
    code: "minor_consent_referral",
    confirmation: false,
  });
};

/**
 * Save eligibility check passed to global store.
 *
 * Persists the birthday and consumerState alongside the "passed" result so
 * that on reopen (or page reload) we can skip the eligibility questions and
 * pre-fill the birthday field later in the insurance form.
 *
 * Also writes the consumer's DOB and state-of-residence to the consumer
 * slice so that downstream checkout (createNewConsumers) can persist them
 * to the consumer record in the database.
 */
export const saveEligibilityPassed = ({
  context,
}: { context: InsuranceConnectorContext }) => {
  const { setEligibilityCheckResult, consumer, setConsumer } =
    useBoundStoreBase.getState();
  setEligibilityCheckResult({
    result: "passed",
    code: null,
    confirmation: false,
    birthday: context.birthday,
    state: context.consumerState,
  });

  if (consumer) {
    const updates: Record<string, string> = {};
    if (context.birthday) {
      updates.dob = parseDateFormat(context.birthday);
    }
    if (context.consumerState) {
      updates.state = context.consumerState.toUpperCase();
    }
    if (Object.keys(updates).length > 0) {
      setConsumer({ ...consumer, ...updates });
    }
  }
};

/**
 * Clear insurance data from global store.
 * Consumer and user data are intentionally preserved — disconnecting
 * insurance should not erase who the patient is.
 */
export const clearStoreInsurance = () => {
  const {
    setInsurances,
    setLastInsuranceCheckResults,
    setIsInsuranceValidated,
  } = useBoundStoreBase.getState();

  setInsurances([]);
  setLastInsuranceCheckResults(null);
  setIsInsuranceValidated(false);
};
